import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/client";
import {
  cancelRefund,
  confirmRefund,
  getPayments,
  getRefundRequests,
  prepareRefund,
  rejectRefund,
} from "../api/endpoints";
import { describeError } from "../api/errors";
import { REFUNDABLE_STATUSES } from "../api/types";
import type { Payment, RefundPreparation } from "../api/types";

export type RefundOutcome = "done" | "stars_failed" | "clawback_failed" | "rejected" | "rejected_unnotified" | null;

interface RefundsState {
  payments: Payment[];
  loading: boolean;
  error: string | null;
  /** Whose payments are shown: null for the queue of requests, a client id for a lookup. */
  userId: number | null;
}

export interface UseRefunds extends RefundsState {
  preparation: RefundPreparation | null;
  preparing: boolean;
  confirming: boolean;
  rejecting: boolean;
  outcome: RefundOutcome;
  confirmError: string | null;
  loadQueue: () => void;
  loadClient: (userId: number) => void;
  select: (payment: Payment) => void;
  dismiss: () => void;
  confirm: () => void;
  reject: () => void;
}

export function useRefunds(): UseRefunds {
  const [state, setState] = useState<RefundsState>({ payments: [], loading: true, error: null, userId: null });
  const [preparation, setPreparation] = useState<RefundPreparation | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [outcome, setOutcome] = useState<RefundOutcome>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  /**
   * The payment currently taken out of the queue for a decision.
   *
   * Held in a ref as well as in state because the cleanup below has to see it at unmount, and
   * state read from an effect cleanup is the value of the render that registered it.
   */
  const prepared = useRef<number | null>(null);
  /**
   * Whether the prepared payment was decided on - confirmed or rejected. Either way it is out of
   * `refunding` and not this dialog's to release: a cancel after a rejection would be refused at
   * best, and after a confirmation it would be wrong.
   */
  const decided = useRef(false);

  const loadQueue = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true, error: null, userId: null }));
    try {
      const page = await getRefundRequests();
      setState({ payments: page.payments, loading: false, error: null, userId: null });
    } catch (error) {
      setState((previous) => ({ ...previous, loading: false, error: describeError(error) }));
    }
  }, []);

  const loadClient = useCallback(async (userId: number) => {
    setState((previous) => ({ ...previous, loading: true, error: null, userId }));
    try {
      const page = await getPayments(userId);
      setState({
        payments: page.payments.filter((payment) => REFUNDABLE_STATUSES.includes(payment.status)),
        loading: false,
        error: null,
        userId,
      });
    } catch (error) {
      setState((previous) => ({ ...previous, loading: false, error: describeError(error), userId }));
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const select = useCallback(async (payment: Payment) => {
    setPreparing(true);
    setConfirmError(null);
    setOutcome(null);
    decided.current = false;
    try {
      const result = await prepareRefund(payment.id);
      prepared.current = payment.id;
      setPreparation(result);
    } catch (error) {
      setConfirmError(describeError(error));
      setPreparation(null);
    } finally {
      setPreparing(false);
    }
  }, []);

  /**
   * Leave the dialog without deciding.
   *
   * This has to release the payment, and it is the easy half to forget: preparing takes it out
   * of the queue, so closing the tab on a prepared refund would leave it stuck in `refunding`
   * where nobody sees it again. The unmount cleanup below is the same call for the same reason.
   */
  const dismiss = useCallback(() => {
    const paymentId = prepared.current;
    prepared.current = null;
    setPreparation(null);
    setConfirmError(null);

    if (paymentId !== null && !decided.current) {
      void cancelRefund(paymentId).catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    return () => {
      const paymentId = prepared.current;
      if (paymentId !== null && !decided.current) {
        void cancelRefund(paymentId).catch(() => undefined);
      }
    };
  }, []);

  const confirm = useCallback(async () => {
    const paymentId = prepared.current;
    if (paymentId === null || confirming || rejecting) {
      return;
    }

    setConfirming(true);
    setConfirmError(null);
    try {
      await confirmRefund(paymentId);
      decided.current = true;
      prepared.current = null;
      setPreparation(null);
      setOutcome("done");
    } catch (error) {
      // the api already put the payment back into the queue when the stars did not come back,
      // and deliberately did not when they did - either way this dialog is done with it
      decided.current = true;
      prepared.current = null;
      setPreparation(null);
      setOutcome(getOutcome(error));
      setConfirmError(describeError(error));
    } finally {
      setConfirming(false);
      if (state.userId === null) {
        void loadQueue();
      } else {
        void loadClient(state.userId);
      }
    }
  }, [confirming, rejecting, loadQueue, loadClient, state.userId]);

  /**
   * Turn the client's request down.
   *
   * Unlike a failed confirmation, a failed rejection leaves the dialog open: the api records the
   * rejection in one step, so a failure means nothing changed - the payment is still held here,
   * and the admin can still confirm, reject again or let it go. A rejection whose message did not
   * reach the client is not a failure: it stands, and the outcome says the client was not told.
   */
  const reject = useCallback(async () => {
    const paymentId = prepared.current;
    if (paymentId === null || confirming || rejecting) {
      return;
    }

    setRejecting(true);
    setConfirmError(null);
    try {
      const result = await rejectRefund(paymentId);
      decided.current = true;
      prepared.current = null;
      setPreparation(null);
      setOutcome(result.notified ? "rejected" : "rejected_unnotified");
      if (state.userId === null) {
        void loadQueue();
      } else {
        void loadClient(state.userId);
      }
    } catch (error) {
      setConfirmError(describeError(error));
    } finally {
      setRejecting(false);
    }
  }, [confirming, rejecting, loadQueue, loadClient, state.userId]);

  return {
    ...state,
    preparation,
    preparing,
    confirming,
    rejecting,
    outcome,
    confirmError,
    loadQueue: () => void loadQueue(),
    loadClient: (userId: number) => void loadClient(userId),
    select: (payment: Payment) => void select(payment),
    dismiss,
    confirm: () => void confirm(),
    reject: () => void reject(),
  };
}

/**
 * Which half of the sequence failed, as far as the admin needs to know.
 *
 * The two failures mean opposite things: after the first nothing was written off and the
 * payment is back in the queue, after the second the stars are already gone and repeating is
 * the right thing to do. The api says which it was - guessing it from the status code was
 * wrong, both are usually 500, and telling an admin the stars came back when they did not is
 * the worse of the two mistakes.
 */
function getOutcome(error: unknown): RefundOutcome {
  return error instanceof ApiError && error.stage === "clawback" ? "clawback_failed" : "stars_failed";
}
